// Shared SEO helpers used by surgery and specialty templates.
// Keeps title/description length in safe SERP ranges and emits BreadcrumbList JSON-LD.

export const SITE_URL = "https://healic.in";
export const BRAND = "Healic Multispeciality Hospital";

function clip(str: string, max: number): string {
  const s = str.replace(/\s+/g, " ").trim();
  if (s.length <= max) return s;
  // Cut at last word boundary before max-1 and append ellipsis
  const slice = s.slice(0, max - 1);
  const cut = slice.lastIndexOf(" ");
  return (cut > max * 0.6 ? slice.slice(0, cut) : slice).trim() + "…";
}

// Strip text in parentheses for shorter titles, keep main brand phrase.
function shortName(name: string): string {
  return name.replace(/\s*\(.*?\)\s*/g, " ").replace(/\s+/g, " ").trim();
}

export type SurgerySeoInput = {
  name: string;
  slug: string;
  short: string;
  overview: string;
  specialtyName?: string;
};

export function surgerySeo(s: SurgerySeoInput) {
  const sn = shortName(s.name);
  // Target: keyword-led, <60 chars where possible.
  const title = clip(`${sn} in India — Cost & Doctors | Healic`, 60);
  const description = clip(
    `${s.short} ${sn} at Healic by senior surgeons with fixed-price packages, cashless insurance & same-day discharge support in India.`,
    158,
  );
  const keywords = [
    sn.toLowerCase(),
    `${sn.toLowerCase()} cost in india`,
    `${sn.toLowerCase()} surgery`,
    `best ${sn.toLowerCase()} doctor`,
    `${sn.toLowerCase()} recovery time`,
    s.specialtyName?.toLowerCase(),
    "healic hospital",
  ]
    .filter(Boolean)
    .join(", ");
  return { title, description, keywords, shortName: sn };
}

export type SpecialtySeoInput = {
  name: string;
  slug: string;
  short: string;
  description: string;
};

export function specialtySeo(s: SpecialtySeoInput) {
  const sn = shortName(s.name);
  const title = clip(`${sn} Hospital — Best Doctors & Surgery | Healic`, 60);
  const description = clip(
    `${s.short} Expert ${sn.toLowerCase()} care at Healic — senior consultants, advanced techniques, cashless insurance and fixed-price surgery packages across India.`,
    158,
  );
  const keywords = [
    sn.toLowerCase(),
    `best ${sn.toLowerCase()} hospital`,
    `${sn.toLowerCase()} doctor near me`,
    `${sn.toLowerCase()} surgery cost`,
    `${sn.toLowerCase()} treatment`,
    "healic hospital",
  ].join(", ");
  return { title, description, keywords, shortName: sn };
}

export function breadcrumbJsonLd(
  items: { name: string; url: string }[],
): { type: string; children: string } {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        item: it.url,
      })),
    }),
  };
}

export function absUrl(path: string): string {
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}
